import { defaultGetUser } from '@/authAccessor';

const region = 'us-central1';
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

// Check if running in emulator mode (same logic as firebase.ts)
const useEmulators = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true';

// Use emulator endpoint if enabled, otherwise production
const baseHost = useEmulators && projectId
  ? `http://127.0.0.1:5001/${projectId}/${region}`
  : projectId
    ? `https://${region}-${projectId}.cloudfunctions.net`
    : null;

type CheckoutBody = {
  plan: 'monthly' | 'annual';
  promo_code?: string; // Match backend snake_case convention
  successUrl?: string;
  cancelUrl?: string;
  returnUrl?: string;
  email?: string; // P0: Support unauthenticated checkout with email
  isPro?: boolean; // Flag for Pro subscription pricing
};

type CheckoutResponse = {
  url: string;
};

type CheckoutErrorResponse = {
  ok: false;
  code: string;
  message: string;
};

// Dev-only debug logger for billing diagnostics
const isDev = import.meta.env.DEV;

const redactEmail = (email: string): string => {
  const [local, domain] = email.split('@');
  return `***@${domain || '***'}`;
};

const logBillingDebug = (label: string, data: Record<string, unknown>) => {
  if (!isDev) return;
  console.debug(`[Billing] ${label}`, data);
};

const assertBaseHost = () => {
  if (baseHost) {
    return baseHost;
  }
  throw new Error('Firebase project ID is not configured.');
};

export async function createCheckoutSessionHttp(body: CheckoutBody): Promise<CheckoutResponse> {
  const user = defaultGetUser();
  // P0: Support unauthenticated checkout with email
  const token = user ? await user.getIdToken() : null;
  const endpoint = `${assertBaseHost()}/createCheckoutSession`;

  // Dev-only: Log redacted request payload
  logBillingDebug('createCheckoutSession request', {
    plan: body.plan,
    promo_code: body.promo_code ? '***' : undefined,
    emailDomain: body.email ? redactEmail(body.email) : undefined,
    isPro: body.isPro,
    hasSuccessUrl: Boolean(body.successUrl),
    hasCancelUrl: Boolean(body.cancelUrl),
  });

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ data: body }), // Wrap in 'data' for onCall v2
  });

  const contentType = response.headers.get('content-type') || '';
  let data: unknown = null;
  let rawText: string | null = null;

  try {
    rawText = await response.text();
    
    // Dev-only: Log raw response
    logBillingDebug('createCheckoutSession response', {
      status: response.status,
      ok: response.ok,
      contentType,
      bodyPreview: rawText.slice(0, 300),
    });

    // Try to parse as JSON
    if (rawText.trim().startsWith('{') || contentType.includes('application/json')) {
      data = JSON.parse(rawText);
    } else {
      // Non-JSON response (HTML error page, plain text, etc.)
      console.error('[createCheckoutSessionHttp] Non-JSON response', {
        status: response.status,
        contentType,
        snippet: rawText.slice(0, 200),
      });
      throw new Error('Billing couldn\'t start. Please try again.');
    }
  } catch (error) {
    // Re-throw our custom errors
    if (error instanceof Error && error.message.includes('Billing')) {
      throw error;
    }
    // JSON parse failure
    console.error('[createCheckoutSessionHttp] JSON parse error', {
      error,
      rawText: rawText?.slice(0, 200),
    });
    throw new Error('Billing couldn\'t start. Please try again.');
  }

  // Firebase onCall v2 wraps response in 'result' field - unwrap it first
  let payload = data;
  if (typeof data === 'object' && data && 'result' in data && typeof (data as Record<string, unknown>).result === 'object') {
    payload = (data as Record<string, unknown>).result as Record<string, unknown>;
  }

  // Handle error responses (structured or legacy)
  if (!response.ok || (typeof payload === 'object' && payload && 'ok' in payload && payload.ok === false)) {
    // Check for structured error response { ok: false, code: ..., message: ... }
    if (typeof payload === 'object' && payload && 'ok' in payload && payload.ok === false) {
      const errorData = payload as CheckoutErrorResponse;
      const message = errorData.message || 'Unable to start checkout.';
      logBillingDebug('createCheckoutSession error', {
        code: errorData.code,
        message: errorData.message,
      });
      throw new Error(message);
    }
    
    // Legacy error format { error: "..." }
    if (typeof payload === 'object' && payload && 'error' in payload && typeof payload.error === 'string') {
      throw new Error(payload.error);
    }
    
    // Fallback
    throw new Error('Unable to start checkout.');
  }

  // Validate success response
  if (typeof payload === 'object' && payload) {
    // New format: { ok: true, url: ... }
    if ('ok' in payload && payload.ok === true && 'url' in payload) {
      return { url: payload.url as string };
    }
    // Legacy format: { url: ... }
    if ('url' in payload && typeof payload.url === 'string') {
      return { url: payload.url };
    }
  }

  throw new Error('Missing checkout URL.');
}
